import { useEffect, useRef } from "react";

const lines = [
  { text: "正しさから降りる場所。", dim: false },
  { text: "こうあるべき、を手放す場所。", dim: true },
  { text: "自分であることを、選び直す場所。", dim: false },
];

export default function ConceptSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);

  /* ── Fractal spiral canvas ── */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    let t = 0;
    const ARMS = 2;
    const PTS = 80;

    const draw = () => {
      const W = canvas.width;
      const H = canvas.height;
      ctx.clearRect(0, 0, W, H);

      /* slow golden glow from right */
      const grd = ctx.createRadialGradient(W * 0.85, H * 0.4, 0, W * 0.85, H * 0.4, W * 0.6);
      grd.addColorStop(0, "rgba(255,200,100,0.06)");
      grd.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = grd;
      ctx.fillRect(0, 0, W, H);

      /* helix arms on right side */
      const cx = W * 0.82;
      const amp = W * 0.1;

      for (let arm = 0; arm < ARMS; arm++) {
        const phaseOffset = (arm / ARMS) * Math.PI * 2;
        ctx.beginPath();
        let first = true;
        for (let i = 0; i <= PTS; i++) {
          const frac = i / PTS;
          const theta = frac * Math.PI * 2 * 5 + t * 0.008 + phaseOffset;
          const x = cx + Math.cos(theta) * amp;
          const y = frac * H;
          const alpha = 0.08 + Math.abs(Math.sin(theta)) * 0.18;
          if (first) { ctx.moveTo(x, y); first = false; }
          else ctx.lineTo(x, y);
          /* glow dot */
          if (i % 8 === 0) {
            const g = ctx.createRadialGradient(x, y, 0, x, y, 6);
            g.addColorStop(0, `rgba(255,220,150,${alpha * 1.8})`);
            g.addColorStop(1, "rgba(255,200,100,0)");
            ctx.save();
            ctx.beginPath();
            ctx.arc(x, y, 6, 0, Math.PI * 2);
            ctx.fillStyle = g;
            ctx.fill();
            ctx.restore();
          }
        }
        ctx.strokeStyle = `rgba(255,210,130,0.12)`;
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      /* connector rungs */
      for (let i = 0; i <= 30; i++) {
        const frac = i / 30;
        const theta = frac * Math.PI * 2 * 5 + t * 0.008;
        const y = frac * H;
        const x1 = cx + Math.cos(theta) * amp;
        const x2 = cx + Math.cos(theta + Math.PI) * amp;
        const a = Math.abs(Math.sin(theta)) * 0.08;
        ctx.beginPath();
        ctx.moveTo(x1, y);
        ctx.lineTo(x2, y);
        ctx.strokeStyle = `rgba(255,220,160,${a})`;
        ctx.lineWidth = 0.5;
        ctx.stroke();
      }

      /* fractal ring pulses */
      for (let r = 0; r < 3; r++) {
        const pulse = ((t * 0.004 + r * 0.33) % 1);
        const radius = pulse * Math.min(W, H) * 0.35;
        const alpha = (1 - pulse) * 0.06;
        ctx.beginPath();
        ctx.arc(cx, H * 0.5, radius, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(255,220,160,${alpha})`;
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      t++;
      animRef.current = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener("resize", resize);
    };
  }, []);

  /* ── Scroll reveal ── */
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const els = entry.target.querySelectorAll(".concept-line");
            els.forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity =
                  el.getAttribute("data-dim") === "true" ? "0.28" : "0.82";
                (el as HTMLElement).style.transform = "translateY(0)";
                (el as HTMLElement).style.filter = "blur(0px)";
              }, i * 500);
            });
            const badge = entry.target.querySelector(".concept-badge");
            if (badge) {
              setTimeout(() => {
                (badge as HTMLElement).style.opacity = "1";
              }, 1600);
            }
          }
        });
      },
      { threshold: 0.12 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="concept"
      ref={sectionRef}
      className="relative w-full flex items-center overflow-hidden"
      style={{ background: "#000000" }}
    >
      {/* top separator */}
      <div className="absolute top-0 left-0 right-0 h-px" style={{ background: "rgba(255,220,140,0.06)" }} />

      {/* canvas */}
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none z-0" />

      {/* subtle texture */}
      <div
        className="absolute inset-0 z-0 pointer-events-none opacity-[0.025]"
        style={{
          backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
          backgroundSize: "200px 200px",
        }}
      />

      <div className="relative z-10 w-full max-w-6xl mx-auto px-6 md:px-20 py-24 md:py-44">
        {/* section label */}
        <p
          className="concept-badge text-white/15 tracking-[0.5em] uppercase mb-14 md:mb-20 text-xs"
          style={{ fontFamily: "'Courier New', monospace", opacity: 0, transition: "opacity 1.5s ease" }}
        >
          — concept
        </p>

        <div className="max-w-2xl flex flex-col gap-10 md:gap-14">
          {lines.map((line, i) => (
            <div key={i} className="flex items-start gap-4 md:gap-6">
              {/* fractal node marker */}
              <div className="flex-shrink-0 mt-2 md:mt-3 flex flex-col items-center gap-1">
                <div
                  style={{
                    width: "5px",
                    height: "5px",
                    borderRadius: "50%",
                    background: line.dim ? "rgba(255,220,140,0.2)" : "rgba(255,220,140,0.55)",
                    boxShadow: line.dim ? "none" : "0 0 8px 2px rgba(255,200,100,0.25)",
                  }}
                />
                {i < lines.length - 1 && (
                  <div style={{ width: "1px", height: "clamp(32px,4vw,56px)", background: "linear-gradient(to bottom, rgba(255,220,140,0.15), transparent)" }} />
                )}
              </div>
              <p
                className="concept-line text-white font-light leading-snug"
                data-dim={line.dim ? "true" : "false"}
                style={{
                  fontSize: "clamp(20px, 3.5vw, 52px)",
                  fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
                  fontWeight: 300,
                  opacity: 0,
                  transform: "translateY(32px)",
                  filter: "blur(8px)",
                  transition: "opacity 2s ease, transform 2s ease, filter 2s ease",
                  letterSpacing: "0.03em",
                  lineHeight: 1.6,
                }}
              >
                {line.text}
              </p>
            </div>
          ))}
        </div>

        {/* bottom ornament */}
        <div className="mt-20 md:mt-28 flex items-center gap-4">
          <div style={{ width: "clamp(40px,5vw,80px)", height: "1px", background: "linear-gradient(to right, rgba(255,220,140,0.3), transparent)" }} />
          <div style={{ width: "4px", height: "4px", borderRadius: "50%", background: "rgba(255,220,140,0.3)" }} />
        </div>
      </div>
    </section>
  );
}
